import type { ReactNode } from "react";
import { Box, Card, CardContent, Chip, Divider, Grid, Stack, Typography } from "@mui/material";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import { Show, useRecordContext, type RaRecord } from "react-admin";

import MiqaatAttendanceAudit from "./MiqaatAttendanceAudit";
import MiqaatRsvpDemographics from "./MiqaatRsvpDemographics";
import MiqaatRsvpHistory from "./MiqaatRsvpHistory";
import MiqaatVenuePosters from "./MiqaatVenuePosters";
import { formatListDate, formatMajlisStartTimeLabel } from "@/utils/date-format";
import { MONTH_NAMES } from "@/utils/hijri-date-utils";

const MEAL_LABELS: Record<string, string> = {
  LUNCH: "Lunch",
  DINNER: "Dinner",
};

const DATE_MODE_LABELS: Record<string, string> = {
  PERMANENT: "Permanent (Hijri annual)",
  TEMPORARY: "One-off (Gregorian date)",
};

const columnSx = {
  width: "100%",
  maxWidth: 1200,
  mr: "auto",
  ml: 0,
  textAlign: "left" as const,
};

function DetailItem({
  label,
  children,
  size = { xs: 12, sm: 6 },
}: {
  label: string;
  children: ReactNode;
  size?: { xs: number; sm?: number; md?: number };
}) {
  return (
    <Grid size={size}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.5, fontWeight: 500 }}
      >
        {label}
      </Typography>
      {children}
    </Grid>
  );
}

function EventSection() {
  const record = useRecordContext<RaRecord>();
  if (!record) {
    return null;
  }

  const dateMode = String(record.dateMode ?? "");
  const isPermanent = dateMode === "PERMANENT";
  const isTemporary = dateMode === "TEMPORARY";
  const hijriMonth = record.hijriMonth;
  const monthLabel =
    typeof hijriMonth === "number" && hijriMonth >= 0 && hijriMonth < 12
      ? MONTH_NAMES.short.en[hijriMonth]
      : "—";
  const englishDateStr =
    isTemporary && record.englishDate ? formatListDate(String(record.englishDate)) : null;

  return (
    <Box sx={columnSx}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 0.7, display: "block", mb: 1 }}
      >
        Event
      </Typography>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 1.5, lineHeight: 1.3 }}>
        {String(record.name ?? "—")}
      </Typography>

      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.5, fontWeight: 500 }}
      >
        Venue
      </Typography>
      <Typography
        variant="body1"
        sx={{ mb: 2, color: record.venue?.trim() ? "text.primary" : "text.disabled" }}
      >
        {record.venue?.trim() ? String(record.venue) : "—"}
      </Typography>

      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.5, fontWeight: 500 }}
      >
        WhatsApp self-scan
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        <Chip
          size="small"
          label={record.selfCheckInEnabled ? "Self-scan on" : "Self-scan off"}
          color={record.selfCheckInEnabled ? "success" : "default"}
          variant="outlined"
        />
        <Chip
          size="small"
          label={
            record.selfCheckInEnabled && record.familySelfCheckInEnabled
              ? "Family check-in on"
              : "Family check-in off"
          }
          color={
            record.selfCheckInEnabled && record.familySelfCheckInEnabled ? "success" : "default"
          }
          variant="outlined"
        />
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
        <CalendarMonthOutlined sx={{ fontSize: 18, color: "text.secondary" }} aria-hidden />
        <Typography variant="subtitle2" fontWeight={600} color="text.primary">
          Date &amp; slot
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <DetailItem label="Type">
          <Chip
            size="small"
            label={(DATE_MODE_LABELS[dateMode] ?? dateMode) || "—"}
            variant="outlined"
            color={isPermanent ? "primary" : "default"}
          />
        </DetailItem>

        <DetailItem label="Meal">
          <Chip
            size="small"
            label={MEAL_LABELS[String(record.mealType)] ?? String(record.mealType ?? "—")}
            sx={{ fontWeight: 600 }}
          />
        </DetailItem>

        <DetailItem label="Start time">
          <Typography variant="body2" fontWeight={500}>
            {formatMajlisStartTimeLabel(
              typeof record.startTime === "string" ? record.startTime : undefined
            )}
          </Typography>
        </DetailItem>

        {isTemporary && (
          <DetailItem label="Gregorian date" size={{ xs: 12 }}>
            <Typography variant="body2" fontWeight={500}>
              {englishDateStr ?? "—"}
            </Typography>
          </DetailItem>
        )}

        {isPermanent && (
          <DetailItem label="Hijri (annual)" size={{ xs: 12 }}>
            <Typography variant="body2" fontWeight={500}>
              {record.hijriDay != null && monthLabel !== "—"
                ? `${record.hijriDay} ${monthLabel}`
                : "—"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 1, lineHeight: 1.5 }}
            >
              Repeats each Hijri year; RSVPs are stored per Gregorian occurrence.
            </Typography>
          </DetailItem>
        )}

        {record.slotTypeDate ? (
          <DetailItem label="WhatsApp RSVP date ({{4}})" size={{ xs: 12 }}>
            <Typography variant="body2" fontWeight={500}>
              {String(record.slotTypeDate)}
            </Typography>
          </DetailItem>
        ) : null}
      </Grid>
    </Box>
  );
}

function DemographicsSection() {
  const record = useRecordContext<RaRecord>();
  if (!record?.id) {
    return null;
  }
  const isPermanent = record.dateMode === "PERMANENT";

  return (
    <Box sx={columnSx}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 0.7, display: "block", mb: 1 }}
      >
        RSVP Demographics
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: isPermanent ? 6 : 12 }}>
          <Card variant="outlined" elevation={0} sx={{ borderRadius: 1, height: "100%" }}>
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                Current RSVP
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                RSVP and scanned demographics for this occurrence.
              </Typography>
              <MiqaatRsvpDemographics embedded />
            </CardContent>
          </Card>
        </Grid>

        {isPermanent && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" elevation={0} sx={{ borderRadius: 1, height: "100%" }}>
              <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Historic RSVP
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Grouped by occurrence date in expandable sections.
                </Typography>
                <MiqaatRsvpHistory embedded />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function AttendanceAuditSection() {
  const record = useRecordContext<RaRecord>();
  if (!record?.id) {
    return null;
  }

  return (
    <Box sx={columnSx}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 0.7, display: "block", mb: 1 }}
      >
        Turnout &amp; Attendance Audit
      </Typography>
      <MiqaatAttendanceAudit embedded />
    </Box>
  );
}

export default function MiqaatShow() {
  return (
    <Show>
      <Box
        component="main"
        sx={{
          width: "100%",
          maxWidth: 1200,
          ml: 0,
          mr: "auto",
          px: { xs: 1.25, sm: 2 },
          py: 1.25,
          pb: 2,
        }}
      >
        <Stack spacing={2} alignItems="flex-start">
          <EventSection />
          <MiqaatVenuePosters />
          <DemographicsSection />
          <AttendanceAuditSection />
        </Stack>
      </Box>
    </Show>
  );
}
